import { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { format, parseISO, parse, isValid, differenceInYears } from 'date-fns';

import ViewComponent from './view_component/index';

import { getDocumentById, createDocument } from 'api/documents';
import { scrollToElement } from 'helpers/scroll';

const DocumentUpdate = ({ match }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [document, setDocument] = useState({});

  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      const d = await getDocumentById(match.params.id);
      const { customer, treatment, fields } = d;

      setDocument({
        ...fields,
        maladie: 'oui',
        prescription: 'ppc',
        prescriptionType: 'initial',
        customerId: customer.id,
        treatmentId: treatment?.id,
        dateOfBirth: fields.dateOfBirth
          ? parse(fields.dateOfBirth, 'dd/MM/yyyy', new Date())
          : new Date(),
        dateSignature: fields.dateSignature
          ? parse(fields.dateSignature, 'dd/MM/yyyy', new Date())
          : new Date(),
      });
    }
    fetchData();
  }, [match.params.id]);

  const [validation, setValidation] = useState({
    name: true,
    carteVitale: true,
    dateOfBirth: true,
    insuredAddress: true,
    insuredRegisstrationNumber: true,
    maladie: true,
    ald: true,
    prescription: true,
    prescriptionType: true,
    cpapDeviceType: true,
    pressure: true,
    stamp: true,
    organisationName: true,
    address: true,
    rpps: true,
    finesId: true,
    dateSignature: true,
    symptoms: true,
    examination: true,
    iah: true,
  });

  const onDateChange = ({ field, value }) => {
    setDocument({
      ...document,
      [field]: value,
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (isSubmitting) {
      setIsSubmitting(false);
    }
    setDocument({
      ...document,
      [name]: value,
    });
  };

  const onCheckboxChange = (e) => {
    const { name } = e.target;

    setDocument({
      ...document,
      [name]: !document[name],
    });
  };

  const onRadioChange = (e) => {
    const { name, id } = e.target;

    setDocument({
      ...document,
      [name]: id,
    });
  };

  const checkValidity = useCallback(() => {
    const validation = {
      name: !!document.name?.trim(),
      carteVitale:
        /^\d{6}[0-9AB]\d{8}$/.test(document.carteVitale?.trim()) &&
        document.carteVitale?.trim().length === 15,
      insuredRegisstrationNumber:
        !document.insuredRegisstrationNumber?.trim() ||
        (/^\d+$/.test(document.insuredRegisstrationNumber?.trim()) &&
          document.insuredRegisstrationNumber?.trim().length === 15),
      dateOfBirth:
        !!document.dateOfBirth &&
        isValid(document.dateOfBirth) &&
        differenceInYears(new Date(), document.dateOfBirth) <= 120 &&
        document.dateOfBirth <= new Date(),
      insuredAddress: !!document.insuredAddress?.trim(),
      maladie: !!document.maladie,
      ald: !!document.ald?.trim(),
      prescription: !!document.prescription,
      prescriptionType: !!document.prescriptionType,
      cpapDeviceType: !!document.cpapDeviceType,
      pressure: !!document.pressure?.trim(),
      stamp: !!document.stamp?.trim(),
      organisationName: !!document.organisationName?.trim(),
      address: !!document.address?.trim(),
      dateSignature: !!document.dateSignature,
      rpps: /^\d+$/.test(document.rpps) && document.rpps.length <= 11,
      finesId:
        /^\d+$/.test(document.finesId?.trim()) &&
        document.finesId.toString().length <= 14,
      symptoms:
        !!document.sleepiness ||
        !!document.dailySnoring ||
        !!document.morningHeadaches ||
        !!document.fatigue ||
        !!document.nocturia ||
        !!document.suffocation ||
        !!document.daytimeSleepiness ||
        !!document.cardiovascularComorbidity ||
        !!document.respiratoryComorbidity ||
        !!document.respiratoryComorbidity,
      examination: !!document.examination,
      iah: /^\d+(\.\d+)?$/.test(document.iah?.trim()),
    };

    const firstFalseyItem = Object.keys(validation).find(
      (i) => validation[i] === false,
    );

    if (firstFalseyItem) {
      scrollToElement(firstFalseyItem);
    }

    setValidation(validation);
    return Object.values(validation).every(Boolean);
  }, [document]);

  const onSubmit = useCallback(async () => {
    setIsSubmitting(true);
    let dateOfBirth, dateSignature;

    if (isValid(document.dateOfBirth)) {
      if (typeof document.dateOfBirth === 'object') {
        dateOfBirth = format(document.dateOfBirth, 'dd/MM/yyyy');
      } else {
        dateOfBirth = format(parseISO(document.dateOfBirth), 'dd/MM/yyyy');
      }
    }

    if (typeof document.dateSignature === 'object') {
      dateSignature = format(document.dateSignature, 'dd/MM/yyyy');
    } else {
      dateSignature = format(parseISO(document.dateSignature), 'dd/MM/yyyy');
    }

    if (checkValidity()) {
      const newDocument = await createDocument({
        type: 'dap',
        fields: {
          ...document,
          dateOfBirth,
          dateSignature,
        },
        withSignature: false,
        customerId: document.customerId,
        treatmentId: document.treatmentId,
      });

      history.push(`/documents/${newDocument.id}`);
    }
  }, [checkValidity, document]);

  const viewProps = {
    onDateChange,
    onChange,
    onCheckboxChange,
    onRadioChange,
    onSubmit,
    document,
    validation,
    isSubmitting,
  };

  return <ViewComponent {...viewProps} />;
};

export default DocumentUpdate;
